import React, { useState } from "react";
import { AltTextImprovements } from "./AltTextImprovements";
import ImageGallery from "./imageGallery";
import { Eye, List } from "lucide-react";
import type { ProductQualityMetric } from "../../types/api";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { addPendingChange } from "../../store/flowSlice";
import { getProductScore } from "../../store/score/getProductScore";
import ImprovementSection from "./ImprovementSection";
import SEOKeywordsSection from "./SeoKeywordsSection";
import { formatBulletsToHtml } from "../ProductsView/utils";
import ProductInfo from "./ProductInfo";
import { getTotalFields } from "./utils";
import { useTranslation } from "../../i18n/hooks";

const ProductDetail = ({
  product,
  jobId,
}: {
  product: ProductQualityMetric;
  jobId: string;
}) => {
  const [showViewButton, setShowViewButton] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const productScore = useSelector((state: RootState) =>
    getProductScore(state.score, product.product_id, jobId)
  );


  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border">

      <div
        className="p-4 flex flex-col sm:flex-row sm:items-center justify-between border-b relative md:sticky top-0 bg-white z-10 gap-4 flex-shrink-0" // Added flex-shrink-0
        onMouseEnter={() => setShowViewButton(true)}
        onMouseLeave={() => setShowViewButton(false)}
      >

        <ProductInfo product={product} productScore={productScore} />

        <div className="flex items-center gap-3 flex-shrink-0">
          {product.product_url && (
            <button
              onClick={() => window.open(product.product_url, "_blank")}
              className={`p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-opacity ${
                showViewButton ? "opacity-100" : "opacity-0"
              }`}
            >
              <Eye className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-6 space-y-6">
          {product.radar_images?.length > 0 && (
            <ImageGallery
              images={product.radar_images}
              productId={product.product_id}
              jobId={jobId}
            />
          )}

          <ImprovementSection
            label={t('jobsView.product_title')}
            oldValue={product.old_title}
            fieldName="title"
            productId={product.product_id}
            jobId={jobId}
            newValue={product.title}
            improvement={productScore.titleImprovement}
            improvements={productScore.titleImprovements}
            scoreComponents={product.total_title_score_components}
            forceShow={showAllFields}
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "title",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <ImprovementSection
            label={t('jobsView.product_description')}
            oldValue={product.old_description_html}
            newValue={product.description_html}
            fieldName="description_html"
            productId={product.product_id}
            jobId={jobId}
            improvement={productScore.descriptionImprovement}
            improvements={productScore.descriptionImprovements}
            scoreComponents={product.total_desc_html_score_components}
            forceShow={showAllFields}
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "description_html",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <ImprovementSection
            label={t('jobsView.description_bullets')}
            oldValue={formatBulletsToHtml(
              product.old_description_bullets || ""
            )}
            newValue={formatBulletsToHtml(product.description_bullets || "")}
            fieldName="description_bullets"
            productId={product.product_id}
            jobId={jobId}
            improvement={0}
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "description_bullets",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <ImprovementSection
            label={t('jobsView.category_name')}
            oldValue={product.old_category_name}
            newValue={product.category_name}
            improvement={productScore.categoryImprovement}
            forceShow={showAllFields}
            fieldName="category_name"
            productId={product.product_id}
            jobId={jobId}
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "category_name",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <ImprovementSection
            label={t('jobsView.category_full_name')}
            oldValue={product.old_category_full_name}
            newValue={product.category_full_name}
            improvement={productScore.categoryFullNameImprovement}
            forceShow={showAllFields}
            fieldName="category_full_name"
            productId={product.product_id}
            jobId={jobId}
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "category_full_name",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <ImprovementSection
            label={t('jobsView.product_type')}
            oldValue={product.old_product_type}
            newValue={product.product_type}
            improvement={productScore.productTypeImprovement}
            forceShow={showAllFields}
            fieldName="product_type"
            productId={product.product_id}
            jobId={jobId}
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "product_type",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <ImprovementSection
            label={t('jobsView.tags')}
            oldValue={product.old_tags}
            newValue={product.tags}
            forceShow={showAllFields}
            fieldName="tags"
            productId={product.product_id}
            jobId={jobId}
            improvement={productScore.tagsImprovement}
            scoreComponents={product.total_tags_score_components} // Different score components
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "tags",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <SEOKeywordsSection
            seo_head_keywords={product.seo_head_keywords}
            seo_body_keywords={product.seo_body_keywords}
            seo_long_tail_keywords={product.seo_long_tail_keywords}
          />

          <ImprovementSection
            label={t('jobsView.seo_title')}
            oldValue={product.old_seo_title}
            newValue={product.seo_title ?? ""}
            improvement={productScore.seoTitleImprovement}
            improvements={productScore.seoTitleImprovements}
            forceShow={showAllFields}
            fieldName="seo_title"
            productId={product.product_id}
            jobId={jobId}
            scoreComponents={product.total_seo_title_score_components} // Different score components
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "seo_title",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <ImprovementSection
            label={t('jobsView.seo_description')}
            oldValue={product.old_seo_description}
            newValue={product.seo_description ?? ""}
            improvement={productScore.seoDescriptionImprovement}
            improvements={productScore.seoDescriptionImprovements}
            forceShow={showAllFields}
            fieldName="seo_description"
            productId={product.product_id}
            jobId={jobId}
            scoreComponents={product.total_seo_description_score_components} // Different score components
            onEdit={async (newValue) => {
              dispatch(
                addPendingChange({
                  enrichmentJobId: jobId,
                  productId: product.product_id,
                  fieldName: "seo_description",
                  fieldValue: newValue,
                })
              );
            }}
          />

          <AltTextImprovements
            product={product}
            productScore={productScore}
            jobId={jobId}
            showAllFields={showAllFields}
          />

          <div className="flex justify-center pt-2">
            <button
              onClick={() => setShowAllFields(!showAllFields)}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors"
            >
              <List className="w-4 h-4" />
              {showAllFields
                ? t('jobsView.hide_unchanged_fields')
                : `${t('jobsView.show_all_fields')} (${getTotalFields(product)} ${t('jobsView.total_fields', { count: getTotalFields(product) })})`}
            </button>
          </div>
        </div>
      </div>{" "}
    </div>
  );
};

export default ProductDetail;
